import { Page, PageTitle } from "@/components/page"
import { BrainIcon, CopyIcon, Plus, Sparkles, TrashIcon, Pencil, Check, X, Filter } from "lucide-react"
import { DataTableSupabase } from "@/components/datatable/supadatatable"
import { DataTable } from "@/components/datatable/datatable"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { DeclarativeMenu } from "@/components/declarativemenu"
import { usePage } from "@/components/page/hooks"
import { supabase } from "@/lib/data"
import { toast } from "sonner"
import { useState, useRef, useEffect, useMemo, useCallback } from "react"
import { useSearchParams } from "react-router-dom"
import { useTableController } from "@/components/datatable/controller"
import { ToMarkdown } from "@/components/ui/tomarkdown"
import { workerRegistry } from "@/lib/agents/registry"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Badge } from "@/components/ui/badge"
import { usePermissions } from "@/lib/hooks/usePermissions"

export const agents = {
  title: "Agents",
  description: "Manage your agents and their configurations.",
  route: "/",
  url: "/",
  icon: BrainIcon,
  component,
  resource: "agents",
  action: "read",
} satisfies PageConfig

function InlineEditableTitle({ agent, tableController, canUpdate }: { agent: Table<"agents">; tableController: { reload: () => void }; canUpdate: boolean }) {
  const [isEditing, setIsEditing] = useState(false)
  const [value, setValue] = useState("")
  const [isSaving, setIsSaving] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const isBlurFromButtonRef = useRef(false)

  useEffect(() => {
    if (isEditing && inputRef.current) {
      setValue(agent.title || "")
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing, agent.title])

  const handleSave = async () => {
    const trimmedValue = value.trim()

    if (trimmedValue === agent.title) {
      setIsEditing(false)
      return
    }

    if (!trimmedValue) {
      toast.error("Agent name cannot be empty")
      setIsEditing(false)
      return
    }

    setIsSaving(true)
    try {
      const { error } = await supabase
        .from("agents")
        .update({ title: trimmedValue })
        .eq("id", agent.id)

      if (error) {
        toast.error(error.message)
        setValue(agent.title)
      } else {
        toast.success("Agent name updated")
        tableController.reload()
      }
    } catch {
      toast.error("Failed to update agent name")
      setValue(agent.title)
    } finally {
      setIsSaving(false)
      setIsEditing(false)
    }
  }

  const handleCancel = () => {
    setIsEditing(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault()
      handleSave()
    } else if (e.key === "Escape") {
      e.preventDefault()
      handleCancel()
    }
  }

  const handleBlur = () => {
    if (!isBlurFromButtonRef.current) {
      handleSave()
    }
    isBlurFromButtonRef.current = false
  }

  const handleButtonClick = (handler: () => void) => (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    isBlurFromButtonRef.current = true
    handler()
  }

  if (isEditing) {
    return (
      <div className="flex items-center gap-2 w-full" onClick={(e) => e.stopPropagation()}>
        <Input
          ref={inputRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          disabled={isSaving}
          className="h-8 flex-1"
        />
        <Button
          size="icon"
          variant="ghost"
          onClick={handleButtonClick(handleSave)}
          disabled={isSaving}
          className="h-6 w-6"
        >
          <Check className="h-3 w-3" />
        </Button>
        <Button
          size="icon"
          variant="ghost"
          onClick={handleButtonClick(handleCancel)}
          disabled={isSaving}
          className="h-6 w-6"
        >
          <X className="h-3 w-3" />
        </Button>
      </div>
    )
  }

  return (
    <div className="flex items-center gap-2 group">
      {/* <span className="flex-1">{agent.title}</span> */}
      <ToMarkdown>{agent.title}</ToMarkdown>
      {canUpdate && (
        <Button
          size="icon"
          variant="ghost"
          onClick={(e) => {
            e.stopPropagation()
            setIsEditing(true)
          }}
          className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <Pencil className="h-3 w-3" />
        </Button>
      )}
    </div>
  )
}

function component() {
  const { team, navigate } = usePage()
  const tableController = useTableController()
  const [searchParams, setSearchParams] = useSearchParams()
  const { canUpdate, canCreate, canDelete } = usePermissions()
  const workerTypeFilter = searchParams.get("worker") || ""

  const workerTypeOptions = useMemo(() => {
    return Object.entries(workerRegistry).map(([key, worker]) => ({
      value: key,
      label: worker.title,
      icon: worker.icon,
    })).sort((a, b) => a.label.localeCompare(b.label))
  }, [])

  const handleWorkerTypeChange = useCallback((value: string) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      if (value === "all" || !value) {
        next.delete("worker")
      } else {
        next.set("worker", value)
      }
      return next
    })
  }, [setSearchParams])

  const columns: Columns<Table<"agents">> = {
    id: { header: "ID", cell: DataTable.cellRender.number, size: 64 },
    title: {
      header: "Title",
      size: 550,
      cell: ({ row }) => <InlineEditableTitle agent={row.original} tableController={tableController} canUpdate={canUpdate("agents")} />,
    },
    team_id: { header: "Team", size: 200, cell: ({ row }) => row.original.team_id?.["name"] },
    description: { header: "Description", size: 300 },
    created_at: { header: "Created", cell: DataTable.cellRender.date, size: 166 },
    evaluation: { header: "Evaluation", size: 100 },
  }

  const menu = [
    { title: "New Agent", action: async () => navigate('/agent/new'), icon: <Plus /> },
    // { title: "From Template", action: async () => {} },
    // { title: "Auto-Generate", action: async () => { }, icon: <Sparkles /> },
  ] satisfies DropdownMenuContents

  const getContextMenu = (agent: Table<"agents">) => {
    const isArchived = (agent.status || "active") === "archived"
    const menu: DropdownMenuContents = []
    if (canCreate("agents")) {
      menu.push({
        title: "Duplicate Agent",
        action: async (agent: Table<"agents">) => {
          const newAgent = { ...agent, team_id: team.id, title: `${agent.title} (copy)`, status: "active" }
          delete newAgent.id
          const { error } = await supabase.from("agents").insert(newAgent as any).select()
          if (error) {
            toast.error(error.message)
          } else {
            toast.success(`A copy of '${agent.title}' was created.`)
          }
        },
        icon: <CopyIcon />
      },
      )
    }

    if (isArchived && canDelete("agents")) {
      menu.push({
        title: "Delete Agent",
        action: async (agent: Table<"agents">) => {
          const { error } = await supabase.from("agents").delete().eq("id", agent.id)
          if (error) {
            toast.error(error.message)
          } else {
            toast.success("Agent deleted permanently")
          }
        },
        ask: "Are you sure you want to delete this agent permanently? This action cannot be undone.",
        icon: <TrashIcon />,
      })
    } else if (!isArchived && canUpdate("agents")) {
      menu.push({
        title: "Archive Agent",
        action: async (agent: Table<"agents">) => {
          const { error } = await supabase.from("agents").update({ status: "archived" }).eq("id", agent.id)
          if (error) {
            toast.error(error.message)
          } else {
            toast.success("Agent archived")
          }
        },
        ask: "Are you sure you want to archive this agent?",
        icon: <TrashIcon />,
      })
    }

    return menu
  }


  return <Page>
    <div className="h-full grid grid-rows-[auto_auto_1fr] gap-4">
      <div className="flex">
        <PageTitle />
        <div className="grow"></div>
        {canCreate("agents") && <div>
          <DeclarativeMenu menu={menu}>
            <Button className="rounded-lg"><Plus className="h-4 w-4" />Create Agent</Button>
          </DeclarativeMenu>
        </div>
        }
      </div>
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Filter by worker:</span>
        </div>
        <Select value={workerTypeFilter || "all"} onValueChange={handleWorkerTypeChange}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="All workers" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All workers</SelectItem>
            {workerTypeOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                <div className="flex items-center gap-2">
                  {option.icon && <option.icon className="h-4 w-4" />}
                  {option.label}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {workerTypeFilter && (
          <Badge variant="secondary" className="cursor-pointer" onClick={() => handleWorkerTypeChange("all")}>
            {workerTypeOptions.find(o => o.value === workerTypeFilter)?.label}
            <X className="h-3 w-3 ml-1" />
          </Badge>
        )}
      </div>
      <DataTableSupabase
        key={`agents-table-${workerTypeFilter}`}
        table="agents"
        columns={columns}
        showSelection
        actions={(canCreate("agents") || canUpdate("agents") || canDelete("agents")) ? getContextMenu : undefined}
        onRowClick={"/agent"}
        sort={["status", "asc"]}
        filter={q => {
          let query = q.eq("team_id", team?.id)
          if (workerTypeFilter) {
            query = query.filter("workers", "cs", `[{"type":"${workerTypeFilter}"}]`)
          }
          return query
        }}
        controller={tableController}
        select={`
        *,
        team_id (
          name
        )
      `}
        enableServerSideFiltering={true}
        searchableColumns={["title", "description"]}
        getRowClassName={(row) => (row.status || "active") === "archived" ? "opacity-50" : undefined}
      />
    </div>
  </Page>

}

